import { useCallback, useEffect, useRef } from 'react';

import { validateSession } from '@enterprise/api';
import { isDefined, isNotDefined, MINUTE } from '@enterprise/core';
import { useEventListener } from '@enterprise/hooks';

import { customEvents } from '@/constants/custom-events';
import { heartbeatThresholdMs } from '@/constants/session-config';
import { useApplicationStore } from '@hooks/application/application-store.hook';

export const useSessionHeartbeat = () => {
  const enterpriseApi = useApplicationStore(({ enterpriseApi }) => {
    return enterpriseApi;
  });
  const lastValidatedAt = useApplicationStore(({ lastValidatedAt }) => {
    return lastValidatedAt;
  });
  const setLastValidatedAt = useApplicationStore(({ setLastValidatedAt }) => {
    return setLastValidatedAt;
  });
  const user = useApplicationStore(({ user }) => {
    return user;
  });

  const documentRef = useRef(document);
  const hiddenAtRef = useRef<null | number>(null);
  const lastValidatedAtRef = useRef(lastValidatedAt);

  useEffect(() => {
    lastValidatedAtRef.current = lastValidatedAt;
  }, [lastValidatedAt]);

  const checkSession = useCallback(async () => {
    try {
      const result = await validateSession(enterpriseApi);

      if (result.failure || isNotDefined(result.data)) {
        throw new Error('Session expired');
      }

      setLastValidatedAt(Date.now());
    } catch {
      window.dispatchEvent(new CustomEvent(customEvents.logOffEvent));
    }
  }, [enterpriseApi, setLastValidatedAt]);

  const handleVisibilityChange = useCallback(
    (_: Event) => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();

        return;
      }

      const hiddenAt = hiddenAtRef.current;

      hiddenAtRef.current = null;

      if (isNotDefined(user) || isNotDefined(hiddenAt)) {
        return;
      }

      const hiddenDuration = Math.max(0, Date.now() - hiddenAt);
      const lastValidated = lastValidatedAtRef.current;
      const elapsedSinceValidation = isDefined(lastValidated) ? Math.max(0, Date.now() - lastValidated) : 0;

      if (hiddenDuration < heartbeatThresholdMs && elapsedSinceValidation < heartbeatThresholdMs) {
        return;
      }

      void checkSession();
    },
    [checkSession, user]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (isNotDefined(user) || document.visibilityState === 'hidden') {
        return;
      }

      const lastValidated = lastValidatedAtRef.current;
      const elapsedSinceValidation = isDefined(lastValidated) ? Math.max(0, Date.now() - lastValidated) : 0;

      if (elapsedSinceValidation < heartbeatThresholdMs) {
        return;
      }

      void checkSession();
    }, MINUTE);

    return () => {
      clearInterval(interval);
    };
  }, [checkSession, user]);

  useEventListener({
    element: documentRef,
    eventName: 'visibilitychange',
    handler: handleVisibilityChange,
  });
};
